import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { errorMessage } from "@/lib/api";
import { invoiceMoney as money } from "@/lib/receivedInvoices";
import {
  hasInvoicePermission,
  invoiceStates,
  invoiceTiles,
  receivedInvoices,
  type Invoice,
} from "@/lib/receivedInvoices";
import "@/components/nabava/ReceivedInvoices.css";
import { InvoiceNavigation } from "@/components/nabava/InvoiceNavigation";

export function ReceivedInvoicesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<Invoice[]>([]),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const allowed = hasInvoicePermission(user);
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    receivedInvoices
      .list(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setRows(data);
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError(errorMessage(cause, "Računi nisu učitani."));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [allowed, reload]);
  if (!allowed) return <Navigate to="/" replace />;
  const tile = invoiceTiles.find((item) => item.key === params.get("stanje")) || invoiceTiles[0]!;
  const refreshed = rows
    .map((row) => row.synced_at)
    .sort()
    .at(-1);
  return (
    <div className="invoice-screen space-y-5 py-4">
      <InvoiceNavigation />
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Ulazni računi</h1>
        <Button
          variant="secondary"
          disabled={loading}
          onClick={() => {
            setLoading(true);
            setError("");
            setReload((value) => value + 1);
          }}
        >
          Osvježi prikaz
        </Button>
      </header>
      {error && <p role="alert">{error}</p>}
      <div className="invoice-tiles" aria-label="Stanja računa">
        {invoiceTiles.map((item) => (
          <Button
            key={item.key}
            variant="secondary"
            data-state={item.key}
            aria-label={`${loading || error ? "—" : rows.filter((row) => item.states.includes(row.stanje)).length} ${item.label}`}
            aria-pressed={item.key === tile.key}
            onClick={() => setParams({ stanje: item.key })}
          >
            <strong>{loading || error ? "—" : rows.filter((row) => item.states.includes(row.stanje)).length}</strong>
            <span>{item.label}</span>
          </Button>
        ))}
      </div>
      <section className="rounded-xl border bg-[var(--color-card)] p-4 space-y-3" aria-busy={loading}>
        <div className="flex flex-wrap justify-between gap-2">
          <h2 className="text-lg font-semibold">{tile.label}</h2>
          <p className="text-sm">
            {refreshed
              ? `Zadnje osvježeno iz Minimaxa: ${new Date(refreshed).toLocaleString("hr-HR", { timeZone: "Europe/Zagreb" })}`
              : "Vrijeme osvježavanja iz Minimaxa nije dostupno."}
          </p>
        </div>
        {loading ? (
          <p role="status">Učitavanje računa…</p>
        ) : (
          !error && (
            <ul className="invoice-list">
              {rows
                .filter((row) => tile.states.includes(row.stanje))
                .map((row) => (
                  <li key={row.id}>
                    <Button
                      variant="secondary"
                      className="invoice-row"
                      onClick={() => navigate(`/nabava/ulazni-racuni/${row.id}`)}
                    >
                      <strong>{row.supplier_name}</strong>
                      <strong>{row.currency === "EUR" ? money(row.amount) : `${row.amount} ${row.currency}`}</strong>
                      <span>
                        {row.number} ·{" "}
                        {row.orders.map((order) => order.broj || "Bez NAR broja").join(", ") || "Bez povezane narudžbe"}
                      </span>
                      <span className="invoice-badge" data-state={row.stanje}>
                        {invoiceStates[row.stanje] || row.stanje}
                      </span>
                    </Button>
                  </li>
                ))}
            </ul>
          )
        )}
        {!loading && !error && !rows.some((row) => tile.states.includes(row.stanje)) && (
          <p>Nema računa u ovom stanju.</p>
        )}
      </section>
    </div>
  );
}
